import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { Baby, Check, ChefHat, ChevronLeft, ChevronRight, Dumbbell, LayoutGrid, MessageSquare, Pill, Pin, Settings as SettingsIcon, Trash2, Utensils, X } from 'lucide-react';
import { HOMEBASE_APPS, MAX_PINNED_APPS, normalizePinnedApps, togglePinnedApp, DEFAULT_DAILY_CONFIG, DEFAULT_MACRO_TARGETS, WORKOUT_DAYS, applyLatestWorkoutAmounts, barbellPerSideFromTotal, barbellPerSideHint, barbellTotalFromPerSide, buildGroceryList, computeRemainingMacros, dailyDoneCount, dailyEnabledItems, dailyHasSkippedItem, dailyItemStatus, dailyOutstandingCount, dailySectionCounts, dailySectionEnabled, dailySectionItems, foodEntryDeleteTarget, formatMacroTotalsLine, formatMealTime, groupMealsByCategory, ingredientToLine, latestWorkoutAmountsByExercise, latestWorkoutByExercise, localDateFromDate, markAllDailyDone, nextNudgeTime, normalizeDailyDay, normalizeMacroTargets, normalizeWorkoutSession, parseIngredientLine, parseJsonResponseBody, recipeIngredientAmount, recipeProteinEmoji, removeFoodEntryFromMeals, resolveFoodDiaryTimeZone, resolveWorkoutExerciseLog, restaurantTasteLabel, setDailyItemStatus, stateBySlug, weekStartDate, workoutProgress } from '@homebase/shared';
import { emptyPregnancyData, normalizePregnancyData, todayIso } from '@homebase/shared/pregnancy';
import { PregnancyApp, pregnancyLauncherStatus } from './pregnancy.jsx';
import '@fontsource-variable/bricolage-grotesque/wdth.css';
import './styles.css';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE;
const LAST_VIEW_KEY = 'homebase.lastView';
const LAST_RECIPE_KEY = 'homebase.lastRecipe';
const INTENDED_PATH_KEY = 'homebase.intendedPath';
const ACTING_AS_KEY = 'homebase.actingAsMember';
const VALID_VIEWS = new Set(['daily', 'pregnancy', 'food', 'recipes', 'workouts', 'settings', 'apps']);
const VIEW_PATHS = { daily: '/daily', pregnancy: '/pregnancy', food: '/food', recipes: '/recipes', workouts: '/workouts', settings: '/settings', apps: '/apps' };

if (SENTRY_DSN) Sentry.init({ dsn: SENTRY_DSN, environment: import.meta.env.MODE, release: SENTRY_RELEASE, tracesSampleRate: 0.1 });

class ApiError extends Error {
  constructor(message, status, body = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

let currentActingAsUserId = null;

function setActingAsUserId(userId) {
  currentActingAsUserId = userId;
  try {
    if (userId) window.localStorage.setItem(ACTING_AS_KEY, userId);
    else window.localStorage.removeItem(ACTING_AS_KEY);
  } catch {}
}

function getStoredActingAsUserId() {
  try {
    return window.localStorage.getItem(ACTING_AS_KEY);
  } catch {
    return null;
  }
}

async function apiRequest(path, options = {}) {
  const hasBody = options.body !== undefined && options.body !== null;
  const actingAsHeader = currentActingAsUserId ? { 'x-acting-as-member': currentActingAsUserId } : {};
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    ...options,
    headers: { ...(hasBody ? { 'Content-Type': 'application/json' } : {}), ...actingAsHeader, ...(options.headers || {}) },
  });
  const text = await response.text();
  let body = null;
  try {
    body = parseJsonResponseBody(text);
  } catch (error) {
    const message = error instanceof Error && error.message === 'api_returned_html'
      ? `API returned HTML instead of JSON (${response.status})`
      : `API returned a non-JSON response (${response.status})`;
    throw new ApiError(message, response.status);
  }
  if (!response.ok) throw new ApiError(body?.message || body?.error || `API ${response.status}`, response.status, body);
  return body;
}

function metaDate(date = new Date()) {
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function recipeTags(recipe) {
  return [recipe.cuisine, recipe.protein, recipe.useCase, ...(recipe.dietTags || [])].filter(Boolean);
}

function scheduledWorkoutDayIndex(date = new Date()) {
  const day = date.getDay();
  if (day === 2) return 1;
  if (day === 4) return 2;
  if (day === 5) return 3;
  return 0;
}

function workoutDateForDay(dayIndex, weekStart) {
  const offsets = [0, 1, 3, 4];
  const date = new Date(`${weekStart}T00:00:00`);
  date.setDate(date.getDate() + (offsets[dayIndex] ?? 0));
  return localDateFromDate(date);
}

function sessionForDay(sessions, dayIndex, weekStart) {
  const date = workoutDateForDay(dayIndex, weekStart);
  return sessions.find((session) => Number(session.dayIndex) === dayIndex && String(session.workoutDate).slice(0, 10) === date);
}

function isPantryIngredient(ingredient) {
  return ingredient.category === 'pantry' || ingredient.pantry === true;
}

function ingredientAmount(ingredient) {
  return recipeIngredientAmount(ingredient);
}

function storedValue(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function viewFromPath(pathname) {
  const path = pathname.replace(/^\/+/, '').toLowerCase().split('/')[0] || '';
  if (VALID_VIEWS.has(path)) return path;
  if (path === 'workout' || path === 'lifts') return 'workouts';
  if (path === 'today') return 'daily';
  if (path === 'baby') return 'pregnancy';
  return null;
}

function initialView() {
  const pathView = viewFromPath(window.location.pathname);
  if (pathView) {
    try { window.sessionStorage.setItem(INTENDED_PATH_KEY, pathView); } catch { /* ignore */ }
    return pathView;
  }
  const intended = (() => { try { return window.sessionStorage.getItem(INTENDED_PATH_KEY); } catch { return null; } })();
  if (intended && VALID_VIEWS.has(intended)) return intended;
  const saved = storedValue(LAST_VIEW_KEY);
  if (saved === 'today') return 'daily';
  return VALID_VIEWS.has(saved) ? saved : 'daily';
}

function App() {
  const query = new URLSearchParams(window.location.search);
  const [cliLoginCode, setCliLoginCode] = useState(() => (query.get('cliLogin') || '').toUpperCase());
  const [cliLoginApproved, setCliLoginApproved] = useState(false);
  const [cliLoginBusy, setCliLoginBusy] = useState(false);
  const [cliLoginStatus, setCliLoginStatus] = useState('');
  const [user, setUser] = useState(null);
  const [sessionUser, setSessionUser] = useState(null);
  const [actingAs, setActingAs] = useState(false);
  const [householdMembers, setHouseholdMembers] = useState([]);
  const [canActAsOthers, setCanActAsOthers] = useState(false);
  const [email, setEmail] = useState(query.get('email') || '');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState('');
  const [dataStatus, setDataStatus] = useState('Loading...');
  const [dataError, setDataError] = useState('');
  const [recipeActionStatus, setRecipeActionStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [recipeStates, setRecipeStates] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [household, setHousehold] = useState(null);
  const [members, setMembers] = useState([]);
  const [plan, setPlan] = useState(null);
  const [groceryList, setGroceryList] = useState(null);
  const [dailyConfig, setDailyConfig] = useState(DEFAULT_DAILY_CONFIG);
  const [dailyDay, setDailyDay] = useState(() => normalizeDailyDay(null, DEFAULT_DAILY_CONFIG));
  const [foodTargets, setFoodTargets] = useState(DEFAULT_MACRO_TARGETS);
  const [foodMeals, setFoodMeals] = useState([]);
  const [foodDate, setFoodDate] = useState(() => localDateFromDate(new Date()));
  const [foodTimeZone, setFoodTimeZone] = useState(() => resolveFoodDiaryTimeZone(DEFAULT_DAILY_CONFIG.timeZone));
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantAreaFilter, setRestaurantAreaFilter] = useState('');
  const [view, setView] = useState(initialView);
  const [selectedRecipeSlug, setSelectedRecipeSlug] = useState(() => storedValue(LAST_RECIPE_KEY));
  const [selectedWorkoutDay, setSelectedWorkoutDay] = useState(() => scheduledWorkoutDayIndex());
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackKind, setFeedbackKind] = useState('bug');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackStatus, setFeedbackStatus] = useState('');
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [pinnedApps, setPinnedApps] = useState(() => normalizePinnedApps(undefined));
  const [pregnancyData, setPregnancyData] = useState(null);
  const [pregnancyUnavailable, setPregnancyUnavailable] = useState(false);
  const preferencesRef = useRef({});
  const pregnancyServerRef = useRef(null);
  const pregnancyPendingRef = useRef([]);
  const pregnancyChainRef = useRef(Promise.resolve());
  const currentWeekStart = weekStartDate();
  const dailyDate = localDateFromDate(new Date());

  useEffect(() => {
    const storedActingAs = getStoredActingAsUserId();
    if (storedActingAs) currentActingAsUserId = storedActingAs;
    void apiRequest('/me').then((result) => {
      setUser(result.user);
      setSessionUser(result.sessionUser || result.user);
      setActingAs(result.actingAs || false);
      setHouseholdMembers(result.householdMembers || []);
      setCanActAsOthers(result.canActAsOthers || false);
    }).catch((error) => { if (error?.status !== 401) setStatus('Could not check session.'); setUser(null); setSessionUser(null); setHouseholdMembers([]); setCanActAsOthers(false); });
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(LAST_VIEW_KEY, view);
    } catch {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
    const targetPath = VIEW_PATHS[view] || '/';
    const currentPath = window.location.pathname;
    if (currentPath !== targetPath) {
      const url = new URL(window.location.href);
      url.pathname = targetPath;
      window.history.replaceState({}, '', url.toString());
    }
  }, [view]);

  useEffect(() => {
    if (user) {
      try { window.sessionStorage.removeItem(INTENDED_PATH_KEY); } catch { /* ignore */ }
    }
  }, [user]);

  useEffect(() => {
    function handlePopState() {
      const pathView = viewFromPath(window.location.pathname);
      if (pathView && pathView !== view) setView(pathView);
    }
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [view]);

  useEffect(() => {
    try {
      if (selectedRecipeSlug) window.localStorage.setItem(LAST_RECIPE_KEY, selectedRecipeSlug);
      else window.localStorage.removeItem(LAST_RECIPE_KEY);
    } catch {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
  }, [selectedRecipeSlug]);

  useEffect(() => {
    if (!user) {
      pregnancyServerRef.current = null; pregnancyPendingRef.current = []; setPregnancyData(null);
      setSessions([]); setRecipeStates([]); setHousehold(null); setMembers([]); setPlan(null); setGroceryList(null); setDailyConfig(DEFAULT_DAILY_CONFIG); setDailyDay(normalizeDailyDay(null, DEFAULT_DAILY_CONFIG)); setFoodTargets(DEFAULT_MACRO_TARGETS); setFoodMeals([]); setRestaurants([]); setDataStatus('Signed out'); setDataError('');
      return;
    }
    void loadAuthedData();
  }, [user]);

  function expireSession(message = 'Session expired. Please sign in again.') {
    setUser(null);
    setSessionUser(null);
    setActingAs(false);
    setHouseholdMembers([]);
    setCanActAsOthers(false);
    setSessions([]);
    setRecipeStates([]);
    setHousehold(null);
    setMembers([]);
    setPlan(null);
    setGroceryList(null);
    setFoodTargets(DEFAULT_MACRO_TARGETS);
    setFoodMeals([]);
    setActingAsUserId(null);
    setStatus(message);
    setDataStatus('Signed out');
  }

  async function switchAccount(targetUserId) {
    if (targetUserId === sessionUser?.id) {
      setActingAsUserId(null);
    } else {
      setActingAsUserId(targetUserId);
    }
    setFoodMeals([]);
    setFoodTargets(DEFAULT_MACRO_TARGETS);
    setDataStatus('Loading...');
    setDataError('');
    try {
      const meResult = await apiRequest('/me');
      setUser(meResult.user);
      setSessionUser(meResult.sessionUser || meResult.user);
      setActingAs(meResult.actingAs || false);
      setHouseholdMembers(meResult.householdMembers || []);
      setCanActAsOthers(meResult.canActAsOthers || false);
      await loadAuthedData();
    } catch (error) {
      if (!handleApiError(error, 'Could not switch account.')) setDataStatus('Error');
    }
  }

  function handleApiError(error, fallback) {
    if (error?.status === 401) { expireSession(); return true; }
    Sentry.captureException(error);
    setDataError(error instanceof Error ? error.message : fallback);
    return false;
  }

  async function loadAuthedData() {
    setDataStatus('Loading...');
    setDataError('');
    try {
      const todayDate = localDateFromDate(new Date());
      const [workoutResult, historyResult, stateResult, householdResult, planResult, groceryResult, recipesResult, dailyConfigResult, dailyDayResult, foodDayResult, restaurantsResult] = await Promise.all([
        apiRequest('/workouts/sessions'),
        apiRequest('/workouts/history'),
        apiRequest('/recipes/state'),
        apiRequest('/household'),
        apiRequest(`/weekly-plan?weekStart=${currentWeekStart}`),
        apiRequest(`/grocery-list?weekStart=${currentWeekStart}`),
        apiRequest('/recipes'),
        apiRequest('/daily/config'),
        apiRequest(`/daily/days/${dailyDate}`),
        apiRequest(`/food-diary/day/${todayDate}`),
        apiRequest('/restaurants'),
      ]);
      setSessions(historyResult.sessions?.length ? historyResult.sessions : workoutResult.sessions || []);
      setRecipeStates(stateResult.states || []);
      setHousehold(householdResult.household || null);
      setMembers(householdResult.members || []);
      setPlan(planResult.plans?.[0] || null);
      setGroceryList(groceryResult.lists?.[0] || null);
      setFoodTargets(normalizeMacroTargets(foodDayResult.targets));
      setFoodMeals(foodDayResult.meals || []);
      setFoodDate(todayDate);
      setFoodTimeZone(resolveFoodDiaryTimeZone(foodDayResult.timeZone ?? dailyConfigResult.config?.timeZone));
      setRestaurants(restaurantsResult.restaurants || []);
      setRecipes(recipesResult.recipes || []);
      setDailyConfig(dailyConfigResult.config);
      setDailyDay(dailyDayResult.data);
      setDataStatus('Loaded');
      void loadAppExtras();
    } catch (error) {
      if (!handleApiError(error, 'Could not load Homebase data.')) setDataStatus('Error');
    }
  }

  // Pins and pregnancy load after the core data and never block it: the web
  // deploys from main before the API, so /pregnancy may not exist yet.
  async function loadAppExtras() {
    const [preferences, pregnancy] = await Promise.all([
      apiRequest('/preferences').catch(() => null),
      apiRequest('/pregnancy').catch((error) => (error?.status === 404 ? 'missing' : null)),
    ]);
    if (preferences) {
      preferencesRef.current = preferences.data || {};
      setPinnedApps(normalizePinnedApps(preferencesRef.current.pinnedApps));
    }
    if (pregnancy === 'missing') { setPregnancyUnavailable(true); return; }
    if (!pregnancy) return;
    setPregnancyUnavailable(false);
    pregnancyServerRef.current = pregnancy.pregnancy ? { data: normalizePregnancyData(pregnancy.pregnancy.data), revision: pregnancy.pregnancy.revision } : null;
    recomputePregnancy();
  }

  async function togglePin(appId) {
    const next = togglePinnedApp(pinnedApps, appId);
    setPinnedApps(next);
    try {
      const latest = await apiRequest('/preferences');
      const data = { ...(latest.data || {}), pinnedApps: next };
      await apiRequest('/preferences', { method: 'PUT', body: JSON.stringify({ data }) });
      preferencesRef.current = data;
    } catch (error) {
      setPinnedApps(pinnedApps);
      handleApiError(error, 'Could not save pinned apps.');
    }
  }

  function recomputePregnancy() {
    const base = pregnancyServerRef.current?.data ?? emptyPregnancyData();
    setPregnancyData(pregnancyPendingRef.current.reduce((data, mutate) => normalizePregnancyData(mutate(structuredClone(data))), base));
  }

  // Saves are queued and each mutation is re-applied to the latest server copy,
  // so two phones editing at once merge instead of overwriting each other.
  function mutatePregnancy(mutate) {
    pregnancyPendingRef.current = [...pregnancyPendingRef.current, mutate];
    recomputePregnancy();
    pregnancyChainRef.current = pregnancyChainRef.current.then(async () => {
      try {
        for (let attempt = 0; attempt < 2; attempt += 1) {
          const base = pregnancyServerRef.current;
          const data = normalizePregnancyData(mutate(structuredClone(base?.data ?? emptyPregnancyData())));
          try {
            const result = await apiRequest('/pregnancy', { method: 'PUT', body: JSON.stringify({ data, baseRevision: base?.revision ?? null }) });
            pregnancyServerRef.current = { data: normalizePregnancyData(result.pregnancy.data), revision: result.pregnancy.revision };
            return;
          } catch (error) {
            if (error?.status === 409 && error.body?.pregnancy && attempt === 0) {
              pregnancyServerRef.current = { data: normalizePregnancyData(error.body.pregnancy.data), revision: error.body.pregnancy.revision };
              continue;
            }
            throw error;
          }
        }
      } catch (error) {
        handleApiError(error, 'Could not save pregnancy changes.');
      } finally {
        const index = pregnancyPendingRef.current.indexOf(mutate);
        if (index !== -1) pregnancyPendingRef.current = pregnancyPendingRef.current.filter((_, i) => i !== index);
        recomputePregnancy();
      }
    });
  }

  function upsertWorkoutSession(data) {
    setSessions((prev) => [
      { workoutDate: data.date, dayIndex: data.dayIdx, data },
      ...prev.filter((session) => !(Number(session.dayIndex) === data.dayIdx && String(session.workoutDate).slice(0, 10) === data.date)),
    ]);
  }

  async function submitAuth(event) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setStatus('Signing in...');
    try {
      const result = await apiRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      setUser(result.user);
      setStatus('Signed in.');
      setDataStatus('Loading...');
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Authentication failed.');
      if (!(error instanceof ApiError && error.status === 400)) Sentry.captureException(error);
    } finally {
      setBusy(false);
    }
  }

  function clearCliLoginParam() {
    const url = new URL(window.location.href);
    url.searchParams.delete('cliLogin');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }

  async function approveCliLogin() {
    if (cliLoginBusy) return;
    setCliLoginBusy(true);
    setCliLoginStatus('Approving...');
    try {
      await apiRequest('/auth/cli/approve', { method: 'POST', body: JSON.stringify({ code: cliLoginCode }) });
      clearCliLoginParam();
      setCliLoginApproved(true);
      setCliLoginStatus('Approved - return to your terminal.');
    } catch (error) {
      if (!handleApiError(error, 'Could not approve this CLI login.')) {
        setCliLoginStatus(error?.status === 404 ? 'That code expired or was already used. Run homebase login again.' : error instanceof Error ? error.message : 'Could not approve this CLI login.');
      }
    } finally {
      setCliLoginBusy(false);
    }
  }

  function dismissCliLogin() {
    clearCliLoginParam();
    setCliLoginCode('');
    setCliLoginApproved(false);
    setCliLoginStatus('');
  }

  async function logout() {
    await apiRequest('/logout', { method: 'POST' }).catch(() => null);
    setUser(null);
    setSessions([]);
  }

  async function saveDailyDay(nextData) {
    const previous = dailyDay;
    setDailyDay(nextData);
    try {
      const result = await apiRequest(`/daily/days/${dailyDate}`, { method: 'PUT', body: JSON.stringify({ data: nextData }) });
      setDailyDay(result.data);
    } catch (error) {
      setDailyDay(previous);
      handleApiError(error, 'Could not save daily checklist.');
    }
  }

  async function updateRecipeState(slug, nextState) {
    setRecipeActionStatus('Saving recipe...');
    try {
      const result = await apiRequest(`/recipes/${encodeURIComponent(slug)}/state`, { method: 'PUT', body: JSON.stringify(nextState) });
      setRecipeStates((states) => [...states.filter((state) => state.slug !== slug), result.state]);
      setRecipeActionStatus('Saved.');
    } catch (error) {
      if (!handleApiError(error, 'Could not save recipe.')) setRecipeActionStatus(error instanceof Error ? error.message : 'Could not save recipe.');
    }
  }

  async function saveRecipe(slug, data) {
    const result = await apiRequest(`/recipes/${encodeURIComponent(slug)}`, { method: 'PUT', body: JSON.stringify({ data }) });
    setRecipes((prev) => prev.map((recipe) => (recipe.slug === slug ? result.recipe : recipe)));
  }

  async function uploadRecipeImage(slug, file) {
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const contentType = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'].includes(file.type) ? file.type : 'image/jpeg';
    const result = await apiRequest(`/recipes/${encodeURIComponent(slug)}/image`, { method: 'POST', body: JSON.stringify({ image: base64, contentType }) });
    setRecipes((prev) => prev.map((recipe) => (recipe.slug === slug ? result.recipe : recipe)));
  }

  async function savePlan(selectedSlugs) {
    setRecipeActionStatus('Saving weekly plan...');
    try {
      const selectedRecipes = recipes.filter((recipe) => selectedSlugs.includes(recipe.slug));
      const data = { selectedSlugs, titles: selectedRecipes.map((recipe) => recipe.title), generatedAt: new Date().toISOString() };
      const result = await apiRequest('/weekly-plan', { method: 'PUT', body: JSON.stringify({ weekStart: currentWeekStart, data }) });
      setPlan(result.plan);
      setRecipeActionStatus('Weekly plan saved.');
    } catch (error) {
      if (!handleApiError(error, 'Could not save weekly plan.')) setRecipeActionStatus(error instanceof Error ? error.message : 'Could not save weekly plan.');
    }
  }

  async function generateAndSaveGroceryList(selectedSlugs) {
    setRecipeActionStatus('Generating grocery list...');
    try {
      const items = buildGroceryList(recipes, selectedSlugs);
      const data = { items, recipeSlugs: selectedSlugs, generatedAt: new Date().toISOString() };
      const result = await apiRequest('/grocery-list', { method: 'PUT', body: JSON.stringify({ weekStart: currentWeekStart, data }) });
      setGroceryList(result.list);
      setRecipeActionStatus('Grocery list saved.');
    } catch (error) {
      if (!handleApiError(error, 'Could not save grocery list.')) setRecipeActionStatus(error instanceof Error ? error.message : 'Could not save grocery list.');
    }
  }

  async function loadFoodDay(date) {
    try {
      const result = await apiRequest(`/food-diary/day/${date}`);
      setFoodMeals(result.meals || []);
      setFoodTargets(normalizeMacroTargets(result.targets));
      setFoodDate(date);
      setFoodTimeZone(resolveFoodDiaryTimeZone(result.timeZone));
    } catch (error) {
      handleApiError(error, 'Could not load food diary.');
    }
  }

  async function loadRestaurants(area = restaurantAreaFilter) {
    try {
      const search = area ? `?area=${encodeURIComponent(area)}` : '';
      const result = await apiRequest(`/restaurants${search}`);
      setRestaurants(result.restaurants || []);
    } catch (error) {
      handleApiError(error, 'Could not load restaurants.');
    }
  }

  async function saveFoodTargets(targets) {
    const previous = foodTargets;
    setFoodTargets(normalizeMacroTargets(targets));
    try {
      const result = await apiRequest('/food-diary/targets', { method: 'PUT', body: JSON.stringify({ targets }) });
      setFoodTargets(normalizeMacroTargets(result.targets));
    } catch (error) {
      setFoodTargets(previous);
      handleApiError(error, 'Could not save macro targets.');
    }
  }

  async function addFoodMeal(meal) {
    try {
      const result = await apiRequest('/food-diary/meals', { method: 'POST', body: JSON.stringify(meal) });
      setFoodMeals((prev) => [result.meal, ...prev].sort((a, b) => new Date(a.eatenAt) - new Date(b.eatenAt)));
      if (result.restaurant || result.visit) await loadRestaurants();
      return result.meal;
    } catch (error) {
      handleApiError(error, 'Could not save meal.');
      return null;
    }
  }

  async function logRestaurantVisit(payload) {
    try {
      await apiRequest('/restaurants/log', { method: 'POST', body: JSON.stringify(payload) });
      await loadRestaurants();
      return true;
    } catch (error) {
      handleApiError(error, 'Could not log restaurant visit.');
      return false;
    }
  }

  async function deleteFoodEntry(entry) {
    const target = foodEntryDeleteTarget(entry);
    const previous = foodMeals;
    setFoodMeals((prev) => removeFoodEntryFromMeals(prev, target));
    try {
      if (target.kind === 'item') {
        await apiRequest(`/food-diary/meals/${target.mealId}/items/${target.itemId}`, { method: 'DELETE' });
        return;
      }
      await apiRequest(`/food-diary/meals/${target.mealId}`, { method: 'DELETE' });
    } catch (error) {
      setFoodMeals(previous);
      handleApiError(error, target.kind === 'item' ? 'Could not delete food item.' : 'Could not delete meal.');
    }
  }

  async function submitFeedback(event) {
    event.preventDefault();
    if (feedbackBusy || !feedbackMessage.trim()) return;
    setFeedbackBusy(true);
    setFeedbackStatus('Sending...');
    try {
      await apiRequest('/feedback', {
        method: 'POST',
        body: JSON.stringify({
          kind: feedbackKind,
          message: feedbackMessage.trim(),
          surface: 'web',
          screen: view,
          url: window.location.href,
          userAgent: navigator.userAgent,
          metadata: { trigger: 'feedback_button' },
        }),
      });
      setFeedbackMessage('');
      setFeedbackStatus('Saved.');
      window.setTimeout(() => setFeedbackOpen(false), 500);
    } catch (error) {
      if (!handleApiError(error, 'Could not send feedback.')) setFeedbackStatus(error instanceof Error ? error.message : 'Could not send feedback.');
    } finally {
      setFeedbackBusy(false);
    }
  }

  const stateMap = useMemo(() => stateBySlug(recipeStates), [recipeStates]);
  const groupedRecipes = useMemo(() => [...recipes].sort((a, b) => Number(Boolean(stateMap[b.slug]?.cookThisWeek || b.activeThisWeek?.enabled)) - Number(Boolean(stateMap[a.slug]?.cookThisWeek || a.activeThisWeek?.enabled)) || a.title.localeCompare(b.title)), [recipes, stateMap]);
  const selectedRecipe = groupedRecipes.find((recipe) => recipe.slug === selectedRecipeSlug) || null;
  const selectedSlugs = useMemo(() => groupedRecipes.filter((recipe) => stateMap[recipe.slug]?.cookThisWeek).map((recipe) => recipe.slug), [groupedRecipes, stateMap]);
  const ready = !dataError && dataStatus !== 'Loading...';

  return (
    <main className={`shell view-${user && !cliLoginCode ? view : 'settings'}`}>
      <header className="topbar">
        <div className="brand"><span className="brandMark" aria-hidden="true"><i /><i /><i /><i /></span>Homebase</div>
        {user ? (
          <div className="account">
            {actingAs ? <span className="actingAsTag">As {user.displayName || user.email}</span> : <span className="accountName">{user.displayName || user.email}</span>}
            {!cliLoginCode ? <button className="feedbackButton" type="button" onClick={() => { setFeedbackStatus(''); setFeedbackOpen(true); }}><MessageSquare size={15} strokeWidth={2.2} />Feedback</button> : null}
            <button className="btnLink" type="button" onClick={logout}>Sign out</button>
          </div>
        ) : null}
      </header>

      {!user ? <AuthPanel busy={busy} email={email} password={password} setEmail={setEmail} setPassword={setPassword} status={status} submitAuth={submitAuth} /> : cliLoginCode ? (
        <CliLoginPanel approved={cliLoginApproved} busy={cliLoginBusy} code={cliLoginCode} status={cliLoginStatus} onApprove={approveCliLogin} onCancel={dismissCliLogin} />
      ) : (
        <>
          {dataStatus === 'Loading...' ? <LoadingBlock message="Loading your Homebase data" /> : null}
          {dataError ? <ErrorBlock message={dataError} onRetry={loadAuthedData} /> : null}
          {view === 'daily' && ready ? <Daily config={dailyConfig} day={dailyDay} onMarkAllDone={() => saveDailyDay(markAllDailyDone(dailyConfig, dailyDay))} onSetStatus={(itemId, status) => saveDailyDay(setDailyItemStatus(dailyDay, itemId, status))} /> : null}
          {view === 'food' && ready ? <FoodDiary date={foodDate} meals={foodMeals} targets={foodTargets} timeZone={foodTimeZone} restaurants={restaurants} restaurantAreaFilter={restaurantAreaFilter} onRestaurantAreaFilter={(value) => { setRestaurantAreaFilter(value); void loadRestaurants(value); }} onAddMeal={addFoodMeal} onLogRestaurant={logRestaurantVisit} onDeleteEntry={deleteFoodEntry} onLoadDay={loadFoodDay} onSaveTargets={saveFoodTargets} /> : null}
          {view === 'recipes' && ready ? <Recipes actionStatus={recipeActionStatus} groceryList={groceryList} plan={plan} recipes={groupedRecipes} selectedRecipe={selectedRecipe} onGenerateGrocery={() => generateAndSaveGroceryList(selectedSlugs)} onSaveRecipe={saveRecipe} onUploadImage={uploadRecipeImage} onSavePlan={() => savePlan(selectedSlugs)} onSelect={setSelectedRecipeSlug} stateMap={stateMap} updateRecipeState={updateRecipeState} /> : null}
          {view === 'workouts' && ready ? <Workout onSessionExpired={expireSession} onSessionSaved={upsertWorkoutSession} sessions={sessions} selectedDay={selectedWorkoutDay} setSelectedDay={setSelectedWorkoutDay} /> : null}
          {view === 'pregnancy' && ready ? (pregnancyData || pregnancyUnavailable ? <PregnancyApp data={pregnancyData ?? emptyPregnancyData()} today={todayIso()} onMutate={mutatePregnancy} unavailable={pregnancyUnavailable} /> : <LoadingBlock message="Loading pregnancy" />) : null}
          {view === 'apps' && ready ? <Launcher pinned={pinnedApps} onOpen={setView} onTogglePin={togglePin} statuses={{
            daily: `${dailyDoneCount(dailyConfig, dailyDay)} of ${dailyEnabledItems(dailyConfig).length} done today`,
            pregnancy: pregnancyUnavailable ? 'Needs API deploy' : pregnancyLauncherStatus(pregnancyData, todayIso()),
            workouts: WORKOUT_DAYS[scheduledWorkoutDayIndex()]?.name,
            food: `${foodMeals.length} meals logged today`,
            recipes: `${selectedSlugs.length} planned this week`,
            settings: household?.name || 'Household',
          }} /> : null}
          {view === 'settings' && ready ? <SettingsPanel household={household} members={members} householdMembers={householdMembers} sessionUser={sessionUser} currentUser={user} actingAs={actingAs} canActAsOthers={canActAsOthers} onSwitchAccount={switchAccount} /> : null}
          <nav className="nav" aria-label="Homebase sections">
            {navApps(pinnedApps, view).map((app) => <TabButton key={app.id} app={app} selected={view === app.id} onClick={() => setView(app.id)} />)}
            <button className={`tab-apps${view === 'apps' ? ' on' : ''}`} type="button" aria-current={view === 'apps' ? 'page' : undefined} onClick={() => setView('apps')}><LayoutGrid size={21} strokeWidth={view === 'apps' ? 2.4 : 2} />Apps</button>
          </nav>
          {feedbackOpen ? <FeedbackDialog busy={feedbackBusy} kind={feedbackKind} message={feedbackMessage} status={feedbackStatus} onChangeKind={setFeedbackKind} onChangeMessage={setFeedbackMessage} onClose={() => setFeedbackOpen(false)} onSubmit={submitFeedback} screen={view} /> : null}
        </>
      )}
    </main>
  );
}

const APP_ICONS = { daily: Pill, pregnancy: Baby, food: Utensils, recipes: ChefHat, workouts: Dumbbell, settings: SettingsIcon };

/** Pinned apps in pin order, plus the open app when it isn't pinned. */
function navApps(pinned, view) {
  const ids = pinned.includes(view) || !APP_ICONS[view] ? pinned : [...pinned, view];
  return ids.map((id) => HOMEBASE_APPS.find((app) => app.id === id)).filter(Boolean);
}

function TabButton({ app, selected, onClick }) {
  const Icon = APP_ICONS[app.id];
  return <button className={`tab-${app.id}${selected ? ' on' : ''}`} type="button" aria-current={selected ? 'page' : undefined} onClick={onClick}><Icon size={21} strokeWidth={selected ? 2.4 : 2} />{app.label}</button>;
}

function Launcher({ pinned, statuses, onOpen, onTogglePin }) {
  const full = pinned.length >= MAX_PINNED_APPS;
  return (
    <section>
      <div className="viewHead">
        <div className="metaStrip"><span>{metaDate()}</span><span>{pinned.length} of {MAX_PINNED_APPS} pinned</span></div>
        <h1 className="pageTitle">Apps</h1>
      </div>
      <div className="appGrid">
        {HOMEBASE_APPS.map((app) => {
          const Icon = APP_ICONS[app.id];
          const isPinned = pinned.includes(app.id);
          return (
            <div className="appTile" key={app.id} style={{ '--tile': app.accent, '--tile-soft': app.accentSoft, '--tile-deep': app.accentDeep }}>
              <button className="appOpen" type="button" onClick={() => onOpen(app.id)}>
                <span className="appIcon"><Icon size={26} strokeWidth={2.1} /></span>
                <span className="appName">{app.label}</span>
                <span className="appStatus">{statuses[app.id] || app.tagline}</span>
              </button>
              <button className="appPin" type="button" disabled={!isPinned && full} title={!isPinned && full ? `Unpin one first (max ${MAX_PINNED_APPS})` : undefined} aria-label={isPinned ? `Unpin ${app.label}` : `Pin ${app.label}`} aria-pressed={isPinned} onClick={() => onTogglePin(app.id)}>
                <Pin size={16} strokeWidth={isPinned ? 2.4 : 2} />
              </button>
            </div>
          );
        })}
      </div>
      <p className="hintBlock">Pinned apps sit in the bottom bar. Everything else is one tap away here.</p>
    </section>
  );
}

function Meter({ value, max, className = '' }) {
  const percent = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return <div className={`meter${value > max && max > 0 ? ' over' : ''}${className ? ` ${className}` : ''}`} role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value}><span style={{ width: `${percent}%` }} /></div>;
}

function MacroRow({ label, value, target, unit = 'g', big }) {
  return (
    <div className={big ? 'macroRow big' : 'macroRow'}>
      <div className="macroTop"><span className="lab">{label}</span><span className={value >= target ? 'val strong' : 'val'}>{value}{unit} / {target}{unit}</span></div>
      <Meter value={value} max={target} />
    </div>
  );
}

function SectionHead({ label, value, live }) {
  return <div className="eyebrowRow"><span className="eyebrow">{label}</span>{value ? <span className={live ? 'count live' : 'count'}>{value}</span> : null}</div>;
}

function LeaderRow({ label, value, valueClass = 'val', onClick }) {
  const content = <><span className="lab">{label}</span><span className="lead" /><span className={valueClass}>{value}</span></>;
  if (onClick) return <button className="leadRow rowMain" type="button" onClick={onClick}>{content}</button>;
  return <div className="leadRow">{content}</div>;
}

function AuthPanel({ busy, email, password, setEmail, setPassword, status, submitAuth }) {
  return (
    <section className="authPanel">
      <div className="viewHead"><h1 className="pageTitle">Sign in</h1></div>
      <p className="bodyText">Sign in with your existing family account.</p>
      <form className="formGrid" onSubmit={submitAuth}>
        <label><span>Email</span><input className="field" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" inputMode="email" required /></label>
        <label><span>Password</span><input className="field" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" type="password" required /></label>
        <button className="btnPrimary" disabled={busy} type="submit">{busy ? 'Signing in' : 'Sign in'}</button>
      </form>
      <div className="statusLine">{status}</div>
    </section>
  );
}

function CliLoginPanel({ approved, busy, code, status, onApprove, onCancel }) {
  return (
    <section className="authPanel">
      <div className="viewHead">
        <div className="eyebrow">CLI login</div>
        <h1 className="pageTitle">{approved ? 'Device approved' : 'Authorize this device to act as you?'}</h1>
      </div>
      <p className="bodyText">{approved ? 'Return to your terminal - the CLI is signed in.' : 'A terminal asked for a Homebase session. Approve only if this code matches the one it printed.'}</p>
      <div className="noteBlock tinted">
        <div className="noteHead"><span className="eyebrow">Code</span><span className={approved ? 'noteValue muted' : 'noteValue'}>{code}</span></div>
        <div className="noteBody">The request expires 10 minutes after the CLI started it.</div>
      </div>
      <div className="formGrid">
        {approved ? <button className="btn" type="button" onClick={onCancel}>Done</button> : (
          <>
            <button className="btnPrimary" disabled={busy} type="button" onClick={onApprove}>{busy ? 'Approving' : 'Approve'}</button>
            <button className="btn" type="button" onClick={onCancel}>Cancel</button>
          </>
        )}
      </div>
      <div className="statusLine">{status}</div>
    </section>
  );
}

const DAILY_SECTION_LABELS = { vitamins: 'Vitamins', drops: 'Allergy drops' };

function dailyClockLabel(value) {
  return new Date(value).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function Daily({ config, day, onMarkAllDone, onSetStatus }) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const counts = dailySectionCounts(config, day);
  const statusById = new Map(day.items.map((item) => [item.itemId, dailyItemStatus(item)]));
  const outstanding = dailyOutstandingCount(config, day);
  const sections = ['vitamins', 'drops'].filter((section) => dailySectionEnabled(config, section));
  const total = sections.reduce((sum, section) => sum + counts[section].total, 0);
  const done = dailyDoneCount(config, day);
  const snoozed = day.snoozedUntil && new Date(day.snoozedUntil) > new Date() ? day.snoozedUntil : null;
  const next = nextNudgeTime(config, day);
  const nudgeValue = day.skippedToday ? 'Skipped' : !outstanding ? 'Clear' : next ? dailyClockLabel(next) : 'None left';
  const nudgeBody = day.skippedToday
    ? 'Skipped today. No more nudges until tomorrow.'
    : !outstanding
      ? `${done} of ${total} done. Nothing left today.`
      : snoozed
        ? `Snoozed until ${dailyClockLabel(snoozed)}.`
        : `Reminders every ${config.repeatEveryHours} h until the list is clear.`;

  if (historyOpen) return <DailyHistory config={config} onBack={() => setHistoryOpen(false)} todayDate={localDateFromDate(new Date())} todayDay={day} />;

  return (
    <section>
      <div className="viewHead">
        <div className="metaStrip"><span>{metaDate()}</span><span>{done} of {total} done</span></div>
        <div className="pageTitleRow">
          <h1 className="pageTitle">Daily</h1>
          <button className="btnTiny" type="button" onClick={() => setHistoryOpen(true)}>History</button>
        </div>
        <Meter value={done} max={total} className="headMeter" />
      </div>
      {sections.length ? sections.map((section) => (
        <div key={section}>
          <SectionHead label={DAILY_SECTION_LABELS[section]} value={`${counts[section].done} / ${counts[section].total}`} live={counts[section].done > 0 && counts[section].done < counts[section].total} />
          <div className="rowGroup">
            {dailySectionItems(config, section).map((item) => <DailyRow item={item} key={item.id} status={statusById.get(item.id) ?? 'pending'} onSetStatus={onSetStatus} />)}
          </div>
        </div>
      )) : <div className="spaced"><EmptyBlock message="Vitamins and allergy drops are both turned off." /></div>}
      <div className="noteBlock accent">
        <div className="noteHead"><span className="eyebrow">Next nudge</span><span className={outstanding && !day.skippedToday ? 'noteValue' : 'noteValue muted'}>{nudgeValue}</span></div>
        <p className="noteBody">{nudgeBody}</p>
      </div>
      <div className="footBlock"><button className="btnPrimary" disabled={!outstanding} type="button" onClick={onMarkAllDone}>Mark all done</button></div>
    </section>
  );
}

function DailyRow({ item, status, onSetStatus }) {
  const done = status === 'done';
  const skipped = status === 'skipped';
  return (
    <div className="dailyRowWrap">
      <button className={done ? 'dailyRow done' : skipped ? 'dailyRow skipped hasAside' : 'dailyRow hasAside'} type="button" aria-pressed={done} onClick={() => onSetStatus(item.id, done ? 'pending' : 'done')}>
        <span className={done ? 'checkbox on' : skipped ? 'checkbox muted' : 'checkbox'}>{done ? <Check size={16} strokeWidth={3.4} /> : null}</span>
        <span className="dailyMain">
          <span className="dailyName">{item.name}</span>
          {item.instruction ? <span className="dailySub">{item.instruction}</span> : null}
        </span>
        {skipped ? null : <span className="dailyDose">{item.dose}</span>}
      </button>
      {status === 'pending' ? (
        <button className="textBtn dailySkip" type="button" aria-label={`Skip ${item.name} today`} onClick={() => onSetStatus(item.id, 'skipped')}>Skip</button>
      ) : status === 'skipped' ? (
        <button className="textBtn dailySkip" type="button" aria-label={`Undo skip ${item.name}`} onClick={() => onSetStatus(item.id, 'pending')}>Undo skip</button>
      ) : null}
    </div>
  );
}

const CAL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const CAL_WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function checkTimeLabel(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function calDetailDateLabel(date) {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function DailyHistory({ config, onBack, todayDate, todayDay }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(todayDate);

  useEffect(() => {
    apiRequest('/daily/summary').then((result) => setRows(result.days ?? [])).catch(() => { setRows([]); setError('Could not load history.'); });
  }, []);

  const statuses = useMemo(() => {
    const total = dailyEnabledItems(config).length;
    const map = new Map();
    for (const row of rows ?? []) {
      const date = String(row.date).slice(0, 10);
      const dayData = normalizeDailyDay(row.data, config);
      const done = dailyDoneCount(config, dayData);
      map.set(date, { day: dayData, done, total, complete: total > 0 && done === total, skipped: dayData.skippedToday || dailyHasSkippedItem(config, dayData) });
    }
    const todayDone = dailyDoneCount(config, todayDay);
    map.set(todayDate, { day: todayDay, done: todayDone, total, complete: total > 0 && todayDone === total, skipped: todayDay.skippedToday || dailyHasSkippedItem(config, todayDay) });
    return map;
  }, [rows, config, todayDate, todayDay]);

  const months = useMemo(() => {
    const keys = new Set([todayDate.slice(0, 7)]);
    for (const date of statuses.keys()) keys.add(date.slice(0, 7));
    return [...keys].sort().reverse();
  }, [statuses, todayDate]);

  const selectedStatus = selected ? statuses.get(selected) : null;

  return (
    <section>
      <button className="backButton" type="button" onClick={onBack}><ChevronLeft size={20} strokeWidth={2.2} />Daily</button>
      <div className="viewHead"><h1 className="pageTitle">History</h1></div>
      {rows === null ? <EmptyBlock message="Loading…" /> : (
        <>
          {error ? <EmptyBlock message={error} /> : null}
          {months.map((month) => {
            const [year, monthNumber] = month.split('-').map(Number);
            const firstWeekday = new Date(year, monthNumber - 1, 1).getDay();
            const dayCount = new Date(year, monthNumber, 0).getDate();
            const cells = [
              ...Array.from({ length: firstWeekday }, () => null),
              ...Array.from({ length: dayCount }, (_, index) => `${month}-${String(index + 1).padStart(2, '0')}`),
            ];
            return (
              <div key={month}>
                <SectionHead label={`${CAL_MONTHS[monthNumber - 1]} ${year}`} />
                <div className="calGrid">
                  {CAL_WEEKDAYS.map((label, index) => <span className="calWeekday" key={index}>{label}</span>)}
                  {cells.map((date, index) => {
                    if (!date) return <span key={index} />;
                    const future = date > todayDate;
                    const status = statuses.get(date);
                    const classes = ['calDay'];
                    if (status?.complete) classes.push('complete');
                    else if (status && status.done > 0) classes.push('partial');
                    if (date === selected) classes.push('selected');
                    if (future) classes.push('future');
                    return (
                      <button className={classes.join(' ')} disabled={future} key={index} type="button" onClick={() => setSelected(date)}>
                        {Number(date.slice(8))}
                        {status?.skipped && !status.complete ? <span className="calSkip">×</span> : null}
                      </button>
                    );
                  })}
                </div>
                {selected?.startsWith(month) && selectedStatus ? (
                  <div className="noteBlock">
                    <div className="noteHead">
                      <span className="eyebrow">{calDetailDateLabel(selected)}</span>
                      <span className={selectedStatus.complete ? 'noteValue' : 'noteValue muted'}>{selectedStatus.skipped && !selectedStatus.done ? 'Skipped' : `${selectedStatus.done} / ${selectedStatus.total}`}</span>
                    </div>
                    {dailyEnabledItems(config).map((item) => {
                      const entry = selectedStatus.day.items.find((dayItem) => dayItem.itemId === item.id);
                      const status = entry ? dailyItemStatus(entry) : 'pending';
                      const value = status === 'done' ? checkTimeLabel(entry.checkedAt) : status === 'skipped' ? 'Skipped' : '—';
                      return <LeaderRow key={item.id} label={item.name} value={value} valueClass={status === 'done' ? 'val strong' : 'val'} />;
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </>
      )}
    </section>
  );
}

function FoodDiary({ date, meals, targets, timeZone, restaurants, restaurantAreaFilter, onRestaurantAreaFilter, onAddMeal, onLogRestaurant, onDeleteEntry, onLoadDay, onSaveTargets }) {
  const [addingMeal, setAddingMeal] = useState(false);
  const [loggingRestaurant, setLoggingRestaurant] = useState(false);
  const [editingTargets, setEditingTargets] = useState(false);
  const [mealForm, setMealForm] = useState({ calories: '', proteinG: '', carbsG: '', fatG: '', notes: '', restaurant: '', restaurantArea: '', rating: '', dishes: '', who: 'both' });
  const [restaurantForm, setRestaurantForm] = useState({ name: '', area: '', rating: 'like', dishes: '', who: 'both', note: '', tabelogUrl: '', tabelogScore: '' });
  const [targetForm, setTargetForm] = useState({ calories: '', proteinG: '', carbsG: '', fatG: '' });
  const [status, setStatus] = useState('');
  const [viewingPhoto, setViewingPhoto] = useState(null);

  const zone = resolveFoodDiaryTimeZone(timeZone);
  const sections = useMemo(() => groupMealsByCategory(meals, zone), [meals, zone]);
  const totals = useMemo(() => sections.reduce((acc, section) => ({
    calories: acc.calories + section.totals.calories,
    proteinG: acc.proteinG + section.totals.proteinG,
    carbsG: acc.carbsG + section.totals.carbsG,
    fatG: acc.fatG + section.totals.fatG,
  }), { calories: 0, proteinG: 0, carbsG: 0, fatG: 0 }), [sections]);
  const remaining = useMemo(() => computeRemainingMacros(totals, targets), [totals, targets]);
  const entryCount = useMemo(() => sections.reduce((count, section) => count + section.entries.length, 0), [sections]);

  function startAddMeal() {
    setMealForm({ calories: '', proteinG: '', carbsG: '', fatG: '', notes: '', restaurant: '', restaurantArea: restaurantAreaFilter || '', rating: '', dishes: '', who: 'both' });
    setStatus('');
    setAddingMeal(true);
  }

  function startLogRestaurant() {
    setRestaurantForm({ name: '', area: restaurantAreaFilter || '', rating: 'like', dishes: '', who: 'both', note: '', tabelogUrl: '', tabelogScore: '' });
    setStatus('');
    setLoggingRestaurant(true);
  }

  function startEditTargets() {
    setTargetForm({
      calories: String(targets.calories),
      proteinG: String(targets.proteinG),
      carbsG: String(targets.carbsG),
      fatG: String(targets.fatG),
    });
    setStatus('');
    setEditingTargets(true);
  }

  async function submitMeal(event) {
    event.preventDefault();
    setStatus('Saving...');
    const meal = {
      calories: mealForm.calories ? Number(mealForm.calories) : null,
      proteinG: mealForm.proteinG ? Number(mealForm.proteinG) : null,
      carbsG: mealForm.carbsG ? Number(mealForm.carbsG) : null,
      fatG: mealForm.fatG ? Number(mealForm.fatG) : null,
      notes: mealForm.notes || null,
      restaurant: mealForm.restaurant || null,
      source: 'manual',
    };
    if (mealForm.restaurant && mealForm.restaurantArea) {
      meal.restaurantLog = {
        name: mealForm.restaurant,
        area: mealForm.restaurantArea,
        who: mealForm.who || 'both',
      };
      if (mealForm.rating) meal.restaurantLog.rating = mealForm.rating;
      if (mealForm.dishes) meal.restaurantLog.dishes = mealForm.dishes;
    }
    const result = await onAddMeal(meal);
    if (result) {
      setAddingMeal(false);
      setStatus('Meal saved.');
    } else {
      setStatus('Could not save meal.');
    }
  }

  async function submitRestaurant(event) {
    event.preventDefault();
    if (!restaurantForm.name.trim() || !restaurantForm.area.trim() || !restaurantForm.rating) {
      setStatus('Name, area, and rating are required.');
      return;
    }
    setStatus('Saving...');
    const payload = {
      name: restaurantForm.name.trim(),
      area: restaurantForm.area.trim(),
      rating: restaurantForm.rating,
      dishes: restaurantForm.dishes.trim(),
      who: restaurantForm.who || 'both',
      note: restaurantForm.note.trim() || null,
      visitedOn: date,
    };
    if (restaurantForm.tabelogUrl.trim()) payload.tabelogUrl = restaurantForm.tabelogUrl.trim();
    if (restaurantForm.tabelogScore.trim()) payload.tabelogScore = restaurantForm.tabelogScore.trim();
    const ok = await onLogRestaurant(payload);
    if (ok) {
      setLoggingRestaurant(false);
      setStatus('Visit logged.');
    } else {
      setStatus('Could not log visit.');
    }
  }

  async function submitTargets(event) {
    event.preventDefault();
    setStatus('Saving...');
    await onSaveTargets({
      calories: Number(targetForm.calories) || targets.calories,
      proteinG: Number(targetForm.proteinG) || targets.proteinG,
      carbsG: Number(targetForm.carbsG) || targets.carbsG,
      fatG: Number(targetForm.fatG) || targets.fatG,
    });
    setEditingTargets(false);
    setStatus('Targets saved.');
  }

  function goToDay(offset) {
    const [year, month, day] = date.split('-').map(Number);
    const d = new Date(year, month - 1, day + offset);
    onLoadDay(localDateFromDate(d));
  }

  if (addingMeal) {
    return (
      <section>
        <button className="backButton" type="button" onClick={() => setAddingMeal(false)}><ChevronLeft size={20} strokeWidth={2.2} />Cancel</button>
        <div className="viewHead">
          <div className="eyebrow">Food diary</div>
          <h1 className="pageTitle">Add meal</h1>
        </div>
        <form className="formGrid" onSubmit={submitMeal}>
          <label><span>Calories</span><input className="field" inputMode="numeric" value={mealForm.calories} onChange={(e) => setMealForm({ ...mealForm, calories: e.target.value })} placeholder="kcal" /></label>
          <label><span>Protein (g)</span><input className="field" inputMode="numeric" value={mealForm.proteinG} onChange={(e) => setMealForm({ ...mealForm, proteinG: e.target.value })} /></label>
          <label><span>Carbs (g)</span><input className="field" inputMode="numeric" value={mealForm.carbsG} onChange={(e) => setMealForm({ ...mealForm, carbsG: e.target.value })} /></label>
          <label><span>Fat (g)</span><input className="field" inputMode="numeric" value={mealForm.fatG} onChange={(e) => setMealForm({ ...mealForm, fatG: e.target.value })} /></label>
          <label><span>Notes</span><textarea className="field" rows={3} value={mealForm.notes} onChange={(e) => setMealForm({ ...mealForm, notes: e.target.value })} placeholder="What did you eat?" /></label>
          <label><span>Restaurant</span><input className="field" value={mealForm.restaurant} onChange={(e) => setMealForm({ ...mealForm, restaurant: e.target.value })} placeholder="Optional — creates/attaches taste log" list="restaurant-name-suggestions" /></label>
          <label><span>Area / city</span><input className="field" value={mealForm.restaurantArea} onChange={(e) => setMealForm({ ...mealForm, restaurantArea: e.target.value })} placeholder="Tokyo, Osaka, …" list="restaurant-area-suggestions" /></label>
          <label><span>Taste rating</span>
            <select className="field" value={mealForm.rating} onChange={(e) => setMealForm({ ...mealForm, rating: e.target.value })}>
              <option value="">Skip</option>
              <option value="like">Like</option>
              <option value="meh">Meh</option>
              <option value="dislike">Dislike</option>
            </select>
          </label>
          <label><span>Dishes</span><input className="field" value={mealForm.dishes} onChange={(e) => setMealForm({ ...mealForm, dishes: e.target.value })} placeholder="What you ordered" /></label>
          <label><span>Who</span>
            <select className="field" value={mealForm.who} onChange={(e) => setMealForm({ ...mealForm, who: e.target.value })}>
              <option value="both">Both</option>
              <option value="ben">Ben</option>
              <option value="linda">Linda</option>
            </select>
          </label>
          <datalist id="restaurant-name-suggestions">{(restaurants || []).map((restaurant) => <option key={restaurant.id} value={restaurant.name} />)}</datalist>
          <datalist id="restaurant-area-suggestions">{[...new Set((restaurants || []).map((restaurant) => restaurant.area))].map((area) => <option key={area} value={area} />)}</datalist>
          <button className="btnPrimary" type="submit">Save meal</button>
        </form>
        <div className="statusLine">{status}</div>
      </section>
    );
  }

  if (loggingRestaurant) {
    return (
      <section>
        <button className="backButton" type="button" onClick={() => setLoggingRestaurant(false)}><ChevronLeft size={20} strokeWidth={2.2} />Cancel</button>
        <div className="viewHead">
          <div className="eyebrow">Taste journal</div>
          <h1 className="pageTitle">Log restaurant</h1>
        </div>
        <form className="formGrid" onSubmit={submitRestaurant}>
          <label><span>Restaurant</span><input className="field" value={restaurantForm.name} onChange={(e) => setRestaurantForm({ ...restaurantForm, name: e.target.value })} required placeholder="Name" /></label>
          <label><span>Area / city</span><input className="field" value={restaurantForm.area} onChange={(e) => setRestaurantForm({ ...restaurantForm, area: e.target.value })} required placeholder="Tokyo" /></label>
          <label><span>Rating</span>
            <select className="field" value={restaurantForm.rating} onChange={(e) => setRestaurantForm({ ...restaurantForm, rating: e.target.value })}>
              <option value="like">Like</option>
              <option value="meh">Meh</option>
              <option value="dislike">Dislike</option>
            </select>
          </label>
          <label><span>Dishes</span><input className="field" value={restaurantForm.dishes} onChange={(e) => setRestaurantForm({ ...restaurantForm, dishes: e.target.value })} placeholder="Ordered…" /></label>
          <label><span>Who</span>
            <select className="field" value={restaurantForm.who} onChange={(e) => setRestaurantForm({ ...restaurantForm, who: e.target.value })}>
              <option value="both">Both</option>
              <option value="ben">Ben</option>
              <option value="linda">Linda</option>
            </select>
          </label>
          <label><span>Note</span><input className="field" value={restaurantForm.note} onChange={(e) => setRestaurantForm({ ...restaurantForm, note: e.target.value })} /></label>
          <label><span>Tabelog URL</span><input className="field" value={restaurantForm.tabelogUrl} onChange={(e) => setRestaurantForm({ ...restaurantForm, tabelogUrl: e.target.value })} placeholder="https://tabelog.com/…" /></label>
          <label><span>Tabelog score</span><input className="field" value={restaurantForm.tabelogScore} onChange={(e) => setRestaurantForm({ ...restaurantForm, tabelogScore: e.target.value })} placeholder="3.51" /></label>
          <button className="btnPrimary" type="submit">Save visit</button>
        </form>
        <div className="statusLine">{status}</div>
      </section>
    );
  }

  if (editingTargets) {
    return (
      <section>
        <button className="backButton" type="button" onClick={() => setEditingTargets(false)}><ChevronLeft size={20} strokeWidth={2.2} />Cancel</button>
        <div className="viewHead">
          <div className="eyebrow">Food diary</div>
          <h1 className="pageTitle">Set targets</h1>
        </div>
        <form className="formGrid" onSubmit={submitTargets}>
          <label><span>Daily calories</span><input className="field" inputMode="numeric" value={targetForm.calories} onChange={(e) => setTargetForm({ ...targetForm, calories: e.target.value })} /></label>
          <label><span>Protein (g)</span><input className="field" inputMode="numeric" value={targetForm.proteinG} onChange={(e) => setTargetForm({ ...targetForm, proteinG: e.target.value })} /></label>
          <label><span>Carbs (g)</span><input className="field" inputMode="numeric" value={targetForm.carbsG} onChange={(e) => setTargetForm({ ...targetForm, carbsG: e.target.value })} /></label>
          <label><span>Fat (g)</span><input className="field" inputMode="numeric" value={targetForm.fatG} onChange={(e) => setTargetForm({ ...targetForm, fatG: e.target.value })} /></label>
          <button className="btnPrimary" type="submit">Save targets</button>
        </form>
        <div className="statusLine">{status}</div>
      </section>
    );
  }

  const dateLabel = (() => {
    const [year, month, day] = date.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' });
  })();

  const caloriePercent = targets.calories ? Math.min(100, Math.round((totals.calories / targets.calories) * 100)) : 0;

  return (
    <section>
      <div className="viewHead">
        <div className="metaStrip">
          <button className="iconBtn" type="button" aria-label="Previous day" onClick={() => goToDay(-1)}><ChevronLeft size={20} strokeWidth={2.2} /></button>
          <span>{dateLabel}</span>
          <button className="iconBtn" type="button" aria-label="Next day" onClick={() => goToDay(1)}><ChevronRight size={20} strokeWidth={2.2} /></button>
        </div>
        <h1 className="pageTitle">Food</h1>
      </div>

      <SectionHead label="Progress" value={`${caloriePercent}% of calories`} live={caloriePercent > 0 && caloriePercent < 100} />
      <div className="rowGroup">
        <MacroRow big label="Calories" value={totals.calories} target={targets.calories} unit=" kcal" />
        <MacroRow label="Protein" value={totals.proteinG} target={targets.proteinG} />
        <MacroRow label="Carbs" value={totals.carbsG} target={targets.carbsG} />
        <MacroRow label="Fat" value={totals.fatG} target={targets.fatG} />
      </div>

      <div className="noteBlock accent">
        <div className="noteHead"><span className="eyebrow">Remaining</span><span className={remaining.calories > 0 ? 'noteValue' : 'noteValue muted'}>{remaining.calories} kcal</span></div>
        <p className="noteBody">{remaining.proteinG}g protein, {remaining.carbsG}g carbs, {remaining.fatG}g fat to go</p>
      </div>

      <div className="actionRow">
        <button className="btn live" type="button" onClick={startAddMeal}>Add meal</button>
        <button className="btn" type="button" onClick={startLogRestaurant}>Log restaurant</button>
        <button className="btn" type="button" onClick={startEditTargets}>Set targets</button>
      </div>
      <div className="statusLine">{status}</div>

      <SectionHead label="Foods" value={String(entryCount)} />
      {sections.length ? (
        sections.map((section) => (
          <div key={section.category}>
            <SectionHead label={section.label} value={formatMacroTotalsLine(section.totals)} />
            <div className="rowGroup">
              {section.entries.map((entry) => (
                <div className="listRow" key={entry.key}>
                  {entry.photoUrl ? <img alt="" className="rowThumb clickableThumb" src={entry.photoUrl} onClick={() => setViewingPhoto(entry.photoUrl)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setViewingPhoto(entry.photoUrl)} /> : null}
                  <div className="rowMain">
                    <span className="rowTop">
                      <span className="lab">{entry.name}</span>
                      <span className="gap" />
                      <span className="val">{entry.calories} kcal</span>
                    </span>
                    <span className="rowMeta">
                      {formatMealTime(entry.eatenAt, zone)}, {entry.proteinG}g protein, {entry.carbsG}g carbs, {entry.fatG}g fat{entry.restaurant ? `, ${entry.restaurant}` : ''}
                    </span>
                  </div>
                  <button className="iconBtn danger" type="button" aria-label={`Delete ${entry.name}`} title="Delete" onClick={() => onDeleteEntry(entry)}><Trash2 size={18} strokeWidth={2} /></button>
                </div>
              ))}
            </div>
          </div>
        ))
      ) : <EmptyBlock message="Nothing logged yet. Add a meal to start today's totals." />}

      <SectionHead label="Restaurants" value={String((restaurants || []).length)} />
      <div className="searchField" style={{ paddingTop: 0, paddingBottom: '10px' }}>
        <input className="field" value={restaurantAreaFilter} onChange={(e) => onRestaurantAreaFilter(e.target.value)} placeholder="Filter by area: Tokyo, Osaka…" aria-label="Filter restaurants by area" />
      </div>
      {(restaurants || []).length ? (
        <div className="rowGroup">
          {(restaurants || []).map((restaurant) => (
            <div className="listRow" key={restaurant.id}>
              <div className="rowMain">
                <span className="rowTop">
                  <span className="lab">{restaurant.name}</span>
                  <span className="gap" />
                  <span className="val">{restaurant.latestVisit ? restaurantTasteLabel(restaurant.latestVisit.rating) : 'Unrated'}</span>
                </span>
                <span className="rowMeta">
                  {restaurant.area}
                  {restaurant.tabelogScore ? ` · Tabelog ${restaurant.tabelogScore}` : ''}
                  {restaurant.latestVisit?.dishes ? ` · ${restaurant.latestVisit.dishes}` : ''}
                  {restaurant.visitCount ? ` · ${restaurant.visitCount} ${restaurant.visitCount === 1 ? 'visit' : 'visits'}` : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : <EmptyBlock message="No restaurants yet. Log a visit to start the taste journal." />}

      {viewingPhoto ? <div className="lightbox" onClick={() => setViewingPhoto(null)} role="presentation"><img alt="" src={viewingPhoto} /><button className="lightboxClose" type="button" onClick={() => setViewingPhoto(null)}>Close</button></div> : null}
    </section>
  );
}

function LoadingBlock({ message }) { return <section className="noticeBlock">{message}</section>; }
function ErrorBlock({ message, onRetry }) { return <section className="noticeBlock errorBlock"><span>{message}</span><button className="btn" type="button" onClick={onRetry}>Retry</button></section>; }
function EmptyBlock({ message }) { return <div className="emptyBlock">{message}</div>; }

const RECIPE_FILTERS = [
  { id: 'all', label: 'All', match: () => true },
  { id: 'week', label: 'This week', match: (recipe, state) => Boolean(state?.cookThisWeek || recipe.activeThisWeek?.enabled) },
  { id: 'liked', label: 'Liked', match: (recipe, state) => state?.rating === 'liked' },
  { id: 'pantry', label: 'Pantry', match: (recipe) => (recipe.ingredients || []).some(isPantryIngredient) },
];

function recipeMatchesSearch(recipe, term) {
  if (!term) return true;
  const haystack = [recipe.title, ...recipeTags(recipe), ...(recipe.ingredients || []).map((ingredient) => ingredient.item)].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(term);
}

function recipeMetaLine(recipe, state) {
  return [recipe.cuisine, recipe.protein, recipe.useCase, state?.rating === 'liked' ? 'liked' : state?.rating === 'disliked' ? 'passed' : null].filter(Boolean).join(' · ');
}

function Recipes({ actionStatus, groceryList, plan, recipes, selectedRecipe, onGenerateGrocery, onSaveRecipe, onSavePlan, onSelect, onUploadImage, stateMap, updateRecipeState }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  if (selectedRecipe) return <RecipeDetail recipe={selectedRecipe} state={stateMap[selectedRecipe.slug]} onBack={() => onSelect(null)} onSaveRecipe={onSaveRecipe} onUploadImage={onUploadImage} updateRecipeState={updateRecipeState} />;
  const active = RECIPE_FILTERS.find((entry) => entry.id === filter) || RECIPE_FILTERS[0];
  const term = search.trim().toLowerCase();
  const visible = recipes.filter((recipe) => recipeMatchesSearch(recipe, term) && active.match(recipe, stateMap[recipe.slug]));
  const planned = visible.filter((recipe) => stateMap[recipe.slug]?.cookThisWeek || recipe.activeThisWeek?.enabled);
  const library = visible.filter((recipe) => !planned.includes(recipe));
  const groceryItems = groceryList?.data?.items || [];
  const planTitles = plan?.data?.titles || [];

  return (
    <section>
      <div className="viewHead">
        <div className="metaStrip"><span>{recipes.filter((recipe) => stateMap[recipe.slug]?.cookThisWeek || recipe.activeThisWeek?.enabled).length} planned this week</span><span>{recipes.length} recipes</span></div>
        <h1 className="pageTitle">Recipes</h1>
      </div>
      <div className="searchField">
        <input className="field" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by title or ingredient" aria-label="Search recipes" />
      </div>
      <div className="chipRow">
        {RECIPE_FILTERS.map((entry) => <button className={entry.id === filter ? 'chip on' : 'chip'} key={entry.id} type="button" onClick={() => setFilter(entry.id)}>{entry.label}</button>)}
      </div>
      <div className="actionRow">
        <button className="btn" type="button" onClick={onSavePlan}>Save weekly plan</button>
        <button className="btn" type="button" onClick={onGenerateGrocery}>Generate grocery list</button>
      </div>
      <div className="statusLine">{actionStatus}</div>

      {planned.length ? (
        <div>
          <SectionHead label="Planned" value={String(planned.length)} live />
          <div className="rowGroup">
            {planned.map((recipe) => <RecipeRow key={recipe.slug} planned recipe={recipe} state={stateMap[recipe.slug]} onSelect={onSelect} updateRecipeState={updateRecipeState} />)}
          </div>
        </div>
      ) : null}

      <SectionHead label="Library" value={String(library.length)} />
      {library.length ? (
        <div className="rowGroup">
          {library.map((recipe) => <RecipeRow key={recipe.slug} recipe={recipe} state={stateMap[recipe.slug]} onSelect={onSelect} updateRecipeState={updateRecipeState} />)}
        </div>
      ) : <EmptyBlock message={term || filter !== 'all' ? 'No recipes match this filter.' : 'No recipes are available yet.'} />}

      {groceryItems.length ? (
        <div>
          <SectionHead label="Grocery list" value={String(groceryItems.length)} />
          <div className="rowGroup">
            {groceryItems.map((item) => <LeaderRow key={item.item} label={item.item} value={item.amount || '—'} />)}
          </div>
        </div>
      ) : null}

      {planTitles.length ? (
        <div>
          <SectionHead label="Saved week plan" value={String(planTitles.length)} />
          <div className="rowGroup">
            {planTitles.map((title, index) => <LeaderRow key={`${index}-${title}`} label={title} value={`#${index + 1}`} />)}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function RecipeRow({ planned, recipe, state, onSelect, updateRecipeState }) {
  const isPlanned = Boolean(state?.cookThisWeek || recipe.activeThisWeek?.enabled);
  const emoji = recipeProteinEmoji(recipe.protein);
  const meta = [recipe.servings ? `Serves ${recipe.servings}` : null, recipeMetaLine(recipe, state)].filter(Boolean).join(' · ');
  return (
    <div className={planned || isPlanned ? 'listRow recipeRow planned' : 'listRow recipeRow'}>
      <button className="rowMain recipeMain" type="button" onClick={() => onSelect(recipe.slug)}>
        {recipe.imageUrl ? <img alt="" className="rowThumb" src={recipe.imageUrl} /> : <span className="rowEmoji" aria-hidden="true">{emoji || '🍽️'}</span>}
        <span className="recipeText">
          <span className="lab">{recipe.title}</span>
          {meta ? <span className="rowMeta">{meta}</span> : null}
        </span>
      </button>
      <div className="rowTools">
        <button className={isPlanned ? 'btnTiny live' : 'btnTiny'} type="button" title="Cook this week" onClick={() => updateRecipeState(recipe.slug, { ...state, cookThisWeek: !isPlanned })}>Week</button>
        <button className={state?.rating === 'liked' ? 'btnTiny on' : 'btnTiny'} type="button" title="Like" onClick={() => updateRecipeState(recipe.slug, { ...state, cookThisWeek: Boolean(state?.cookThisWeek), rating: state?.rating === 'liked' ? null : 'liked' })}>Like</button>
        <button className={state?.rating === 'disliked' ? 'btnTiny on' : 'btnTiny'} type="button" title="Dislike" onClick={() => updateRecipeState(recipe.slug, { ...state, cookThisWeek: Boolean(state?.cookThisWeek), rating: state?.rating === 'disliked' ? null : 'disliked' })}>Pass</button>
      </div>
    </div>
  );
}

function RecipeDetail({ recipe, state, onBack, onSaveRecipe, onUploadImage, updateRecipeState }) {
  const buyIngredients = (recipe.ingredients || []).filter((ingredient) => !isPantryIngredient(ingredient));
  const pantryIngredients = (recipe.ingredients || []).filter(isPantryIngredient);
  const [editing, setEditing] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [tab, setTab] = useState('ingredients');
  const [form, setForm] = useState(null);
  const planned = Boolean(state?.cookThisWeek || recipe.activeThisWeek?.enabled);

  function startEditing() {
    setForm({
      title: recipe.title || '',
      cuisine: recipe.cuisine || '',
      protein: recipe.protein || '',
      servings: recipe.servings != null ? String(recipe.servings) : '',
      ingredients: (recipe.ingredients || []).map(ingredientToLine).join('\n'),
      instructions: (recipe.instructions || []).join('\n'),
    });
    setEditStatus('');
    setEditing(true);
  }

  async function submitEdit(event) {
    event.preventDefault();
    setEditStatus('Saving...');
    try {
      await onSaveRecipe(recipe.slug, {
        ...recipe,
        title: form.title.trim() || 'Untitled',
        cuisine: form.cuisine.trim() || undefined,
        protein: form.protein.trim() || undefined,
        servings: form.servings.trim() || undefined,
        ingredients: form.ingredients.split('\n').map(parseIngredientLine).filter(Boolean),
        instructions: form.instructions.split('\n').map((line) => line.trim()).filter(Boolean),
      });
      setEditing(false);
    } catch (error) { setEditStatus(error instanceof Error ? error.message : 'Could not save.'); }
  }

  if (editing && form) {
    return (
      <section>
        <button className="backButton" type="button" onClick={() => setEditing(false)}><ChevronLeft size={20} strokeWidth={2.2} />Cancel</button>
        <div className="viewHead">
          <div className="eyebrow">Edit recipe</div>
          <h1 className="detailTitle">{form.title || 'Untitled'}</h1>
        </div>
        <form className="formGrid" onSubmit={submitEdit}>
          <label><span>Title</span><input className="field" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
          <label><span>Cuisine</span><input className="field" value={form.cuisine} onChange={(e) => setForm({ ...form, cuisine: e.target.value })} /></label>
          <label><span>Protein</span><input className="field" value={form.protein} onChange={(e) => setForm({ ...form, protein: e.target.value })} /></label>
          <label><span>Servings</span><input className="field" value={form.servings} onChange={(e) => setForm({ ...form, servings: e.target.value })} /></label>
          <label><span>Ingredients, one per line</span><textarea className="field" rows={10} value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} /></label>
          <label><span>Method, one step per line</span><textarea className="field" rows={8} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} /></label>
          <div className="actionRow" style={{ padding: 0 }}>
            <button className="btn live" type="submit">Save</button>
            <label className="btn" style={{ cursor: 'pointer' }}>{recipe.imageUrl ? 'Replace photo' : 'Add photo'}<input accept="image/*" hidden type="file" onChange={async (e) => { const file = e.target.files?.[0]; if (!file) return; setEditStatus('Uploading photo...'); try { await onUploadImage(recipe.slug, file); setEditStatus('Photo saved.'); } catch (error) { setEditStatus(error instanceof Error ? error.message : 'Could not upload.'); } }} /></label>
          </div>
        </form>
        <div className="statusLine">{editStatus}</div>
      </section>
    );
  }

  const facts = [recipe.servings ? `Serves ${recipe.servings}` : null, ...recipeTags(recipe).slice(0, 4), state?.rating === 'liked' ? 'Liked' : null].filter(Boolean);

  return (
    <section>
      <button className="backButton" type="button" onClick={onBack}><ChevronLeft size={20} strokeWidth={2.2} />Recipes</button>
      <div className="viewHead">
        {planned ? <div className="eyebrow live">Planned this week</div> : null}
        <h1 className="detailTitle">{[recipeProteinEmoji(recipe.protein), recipe.title].filter(Boolean).join(' ')}</h1>
      </div>
      <div className="factRow">{facts.map((fact) => <span key={fact}>{fact}</span>)}</div>
      <div className="photoBand">{recipe.imageUrl ? <img alt="" src={recipe.imageUrl} /> : <span>No photo yet. Add one from Edit.</span>}</div>
      <div className="actionRow" style={{ paddingTop: 0, paddingBottom: '16px' }}>
        <button className={planned ? 'btn live' : 'btn'} type="button" onClick={() => updateRecipeState(recipe.slug, { ...state, cookThisWeek: !state?.cookThisWeek })}>{planned ? 'Planned this week' : 'Cook this week'}</button>
        <button className={state?.rating === 'liked' ? 'btn on' : 'btn'} type="button" onClick={() => updateRecipeState(recipe.slug, { ...state, cookThisWeek: Boolean(state?.cookThisWeek), rating: state?.rating === 'liked' ? null : 'liked' })}>Like</button>
        <button className="btn" type="button" onClick={startEditing}>Edit</button>
      </div>
      <div className="subTabs">
        <button className={tab === 'ingredients' ? 'on' : ''} type="button" onClick={() => setTab('ingredients')}>Ingredients</button>
        <button className={tab === 'method' ? 'on' : ''} type="button" onClick={() => setTab('method')}>Method</button>
        <button className={tab === 'notes' ? 'on' : ''} type="button" onClick={() => setTab('notes')}>Notes</button>
      </div>
      {tab === 'ingredients' ? (
        <>
          <IngredientSection title="To buy" ingredients={buyIngredients} />
          <IngredientSection title="Pantry check" ingredients={pantryIngredients} />
          {!buyIngredients.length && !pantryIngredients.length ? <div className="spaced"><EmptyBlock message="No ingredients recorded." /></div> : null}
        </>
      ) : null}
      {tab === 'method' ? (
        (recipe.instructions || []).length ? (
          <>
            <SectionHead label="Method" value={String(recipe.instructions.length)} />
            <ol className="methodList">
              {recipe.instructions.map((step, index) => <li className="methodStep" key={`${index}-${step}`}><span className="n">{index + 1}</span><p className="bodyText">{step}</p></li>)}
            </ol>
          </>
        ) : <div className="spaced"><EmptyBlock message="No method steps recorded." /></div>
      ) : null}
      {tab === 'notes' ? (
        (recipe.servingNotes || []).length ? (
          <>
            <SectionHead label="Serving notes" value={String(recipe.servingNotes.length)} />
            <div className="rowGroup">
              {recipe.servingNotes.map((note, index) => <div className="noteRow" key={`${index}-${note}`}><p className="bodyText">{note}</p></div>)}
            </div>
          </>
        ) : <div className="spaced"><EmptyBlock message="No serving notes recorded." /></div>
      ) : null}
    </section>
  );
}

function IngredientSection({ title, ingredients }) {
  if (!ingredients.length) return null;
  return (
    <div>
      <SectionHead label={title} value={String(ingredients.length)} />
      <div className="rowGroup">
        {ingredients.map((ingredient, index) => (
          <div className="leadRow" key={`${ingredient.item}-${index}`}>
            <span className="lab">{ingredient.item}{ingredient.optional ? <small className="optional">optional</small> : null}</span>
            <span className="lead" />
            <span className="val">{ingredientAmount(ingredient) || '—'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Workout({ onSessionExpired, onSessionSaved, sessions, selectedDay, setSelectedDay }) {
  const day = WORKOUT_DAYS[selectedDay];
  const currentWeekStart = weekStartDate();
  const latest = useMemo(() => latestWorkoutByExercise(sessions), [sessions]);
  const latestAmounts = useMemo(() => latestWorkoutAmountsByExercise(sessions), [sessions]);
  function draftForDay(dayIndex) {
    return applyLatestWorkoutAmounts(normalizeWorkoutSession(sessionForDay(sessions, dayIndex, currentWeekStart)?.data, dayIndex, workoutDateForDay(dayIndex, currentWeekStart)), latestAmounts);
  }
  const [storedDraft, setStoredDraft] = useState(() => draftForDay(selectedDay));
  const [saveState, setSaveState] = useState('Saved');
  const saveTimerRef = useRef(null);
  const pendingSaveRef = useRef(null);
  // Rebuild in this render, not an effect: otherwise one committed frame pairs
  // the new day's exercises with the previous day's logs.
  const draft = storedDraft.dayIdx === selectedDay ? storedDraft : draftForDay(selectedDay);
  if (draft !== storedDraft) setStoredDraft(draft);
  const progress = workoutProgress(selectedDay, draft);

  function dayCompleted(index) {
    const data = index === selectedDay ? draft : normalizeWorkoutSession(sessionForDay(sessions, index, currentWeekStart)?.data, index, workoutDateForDay(index, currentWeekStart));
    const dayProgress = workoutProgress(index, data);
    return dayProgress.totalSets > 0 && dayProgress.doneSets === dayProgress.totalSets;
  }

  useEffect(() => {
    if (pendingSaveRef.current) return;
    setStoredDraft(draftForDay(selectedDay));
  }, [sessions, latestAmounts]);

  useEffect(() => () => { void flushSave(); }, []);

  async function save(nextDraft) {
    onSessionSaved(nextDraft);
    try {
      await apiRequest(`/workouts/sessions/${nextDraft.date}/${nextDraft.dayIdx}`, { method: 'PUT', body: JSON.stringify({ data: nextDraft }) });
      setSaveState('Saved');
    } catch (error) {
      if (error?.status === 401) { onSessionExpired(); return; }
      setSaveState('Save failed');
      Sentry.captureException(error);
    }
  }

  function flushSave() {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    const pending = pendingSaveRef.current;
    pendingSaveRef.current = null;
    return pending ? save(pending) : Promise.resolve();
  }

  function queueSave(nextDraft) {
    setStoredDraft(nextDraft);
    setSaveState('Saving...');
    pendingSaveRef.current = nextDraft;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { saveTimerRef.current = null; void flushSave(); }, 650);
  }

  function selectDay(index) {
    if (index === selectedDay) return;
    void flushSave();
    setSelectedDay(index);
  }

  function updateSet(exerciseId, setIndex, field, value) {
    const next = structuredClone(draft);
    const exercise = day.exercises.find((entry) => entry.id === exerciseId);
    if (!exercise) return;
    next.exercises[exerciseId] = resolveWorkoutExerciseLog(exercise, next.exercises[exerciseId]);
    const updateLoad = (set) => {
      set.load = value;
      set.perSideLoad = barbellPerSideFromTotal(value);
    };
    const updatePerSideLoad = (set) => {
      set.perSideLoad = value;
      set.load = barbellTotalFromPerSide(value);
    };
    if (field === 'load' && setIndex === 0) next.exercises[exerciseId].sets.forEach(updateLoad);
    else if (field === 'perSideLoad' && setIndex === 0) next.exercises[exerciseId].sets.forEach(updatePerSideLoad);
    else if (field === 'load') updateLoad(next.exercises[exerciseId].sets[setIndex]);
    else if (field === 'perSideLoad') updatePerSideLoad(next.exercises[exerciseId].sets[setIndex]);
    else next.exercises[exerciseId].sets[setIndex][field] = value;
    queueSave(next);
  }

  function toggleSet(exerciseId, setIndex) {
    const next = structuredClone(draft);
    const exercise = day.exercises.find((entry) => entry.id === exerciseId);
    if (!exercise) return;
    next.exercises[exerciseId] = resolveWorkoutExerciseLog(exercise, next.exercises[exerciseId]);
    const set = next.exercises[exerciseId].sets[setIndex];
    if (!set) return;
    set.done = !set.done;
    const nextProgress = workoutProgress(next.dayIdx, next);
    if (nextProgress.doneSets === nextProgress.totalSets) next.completedAt = new Date().toISOString();
    else delete next.completedAt;
    queueSave(next);
  }

  return (
    <section>
      <div className="viewHead">
        <div className="metaStrip"><span>{day.focus}</span><span className={saveState === 'Saved' ? 'saveState' : saveState === 'Save failed' ? 'saveState failed' : 'saveState pending'}>{saveState}</span></div>
        <h1 className="pageTitle">{day.name}</h1>
        <div className="metaStrip"><span>{metaDate()}</span><span>{progress.doneSets} of {progress.totalSets} sets</span></div>
        <Meter value={progress.doneSets} max={progress.totalSets} />
      </div>
      <div className="dayBar">
        {WORKOUT_DAYS.map((workoutDay, index) => (
          <button className={index === selectedDay ? 'on' : ''} key={workoutDay.short} type="button" onClick={() => selectDay(index)}>
            <b>{workoutDay.short}{dayCompleted(index) ? <Check className="doneMark" size={14} strokeWidth={3.4} style={{ marginLeft: 4, verticalAlign: -1 }} /> : null}</b>
            <span>{workoutDay.weekday.slice(0, 3)}</span>
          </button>
        ))}
      </div>
      <SectionHead label="Prep" value={String(day.prep.items.length)} />
      <div className="rowGroup">
        {day.prep.items.map((item) => <div className="noteRow" key={item}><p className="bodyText">{item}</p></div>)}
      </div>
      {day.exercises.map((exercise) => <ExerciseLog exercise={exercise} key={exercise.id} latest={latest[exercise.id]} log={draft.exercises[exercise.id]} onToggle={toggleSet} onUpdate={updateSet} />)}
    </section>
  );
}

function ExerciseLog({ exercise, latest, log, onToggle, onUpdate }) {
  const resolvedLog = resolveWorkoutExerciseLog(exercise, log);
  const loadHint = barbellPerSideHint(resolvedLog.sets?.[0]?.load ?? '');
  const activeIndex = resolvedLog.sets.findIndex((set) => !set.done);
  const prev = (latest?.log?.sets ?? []).map((set) => [set.load, set.reps].filter(Boolean).join('×'));
  return (
    <div className="exerciseBlock">
      <SectionHead label={exercise.name} value={`${exercise.sets}×${exercise.reps}, RIR ${exercise.rir}`} />
      <div className="exerciseCard">
      {exercise.variants ? <div className="variantLine">{exercise.variants}</div> : null}
      <div className="setHead">
        <span>#</span>
        <span className="setPrevCol">Prev</span>
        <span>Load</span>
        <span>Per side</span>
        <span>Reps</span>
        <span />
      </div>
      {resolvedLog.sets.map((set, index) => (
        <div className={`setRow${set.done ? ' logged' : ''}${index === activeIndex ? ' active' : ''}`} key={index}>
          <span className="setIndex">{index + 1}</span>
          <span className="setPrev">{prev[index] || '—'}</span>
          <input className="setInput" inputMode="decimal" value={set.load} onChange={(event) => onUpdate(exercise.id, index, 'load', event.target.value)} placeholder="Total lb" aria-label={`Set ${index + 1} load`} />
          <input className="setInput" inputMode="decimal" value={set.perSideLoad ?? barbellPerSideFromTotal(set.load)} onChange={(event) => onUpdate(exercise.id, index, 'perSideLoad', event.target.value)} placeholder="Per side" aria-label={`Set ${index + 1} per side`} />
          <input className="setInput" inputMode="numeric" value={set.reps} onChange={(event) => onUpdate(exercise.id, index, 'reps', event.target.value)} aria-label={`Set ${index + 1} reps`} />
          <button className={set.done ? 'setCheck on' : 'setCheck'} type="button" onClick={() => onToggle(exercise.id, index)} aria-label={`Toggle set ${index + 1}`}><Check size={16} strokeWidth={3} /></button>
        </div>
      ))}
      </div>
      {latest ? <div className="hint">Last time {prev.filter(Boolean).join(', ') || '—'} on {String(latest.workoutDate).slice(0, 10)}</div> : null}
      {loadHint ? <div className="hint live">{loadHint}</div> : null}
    </div>
  );
}

function SettingsPanel({ household, members, householdMembers, sessionUser, currentUser, actingAs, canActAsOthers, onSwitchAccount }) {
  return (
    <section>
      <div className="viewHead">
        <div className="metaStrip"><span>{household?.name || 'Homebase'}</span></div>
        <h1 className="pageTitle">Household</h1>
      </div>

      {canActAsOthers ? (
        <>
          <SectionHead label="Switch account" value={actingAs ? 'Acting as another' : ''} live={actingAs} />
          <div className="rowGroup">
            {householdMembers.map((member) => {
              const isSessionUser = member.id === sessionUser?.id;
              const isCurrentUser = member.id === currentUser?.id;
              return (
                <button
                  key={member.id}
                  className={isCurrentUser ? 'leadRow rowMain selected' : 'leadRow rowMain'}
                  type="button"
                  onClick={() => onSwitchAccount(member.id)}
                  disabled={isCurrentUser}
                >
                  <span className="lab">{member.displayName || member.email}{isSessionUser ? ' (you)' : ''}</span>
                  <span className="lead" />
                  <span className={isCurrentUser ? 'val strong' : 'val'}>{isCurrentUser ? 'Active' : 'Switch'}</span>
                </button>
              );
            })}
          </div>
          {actingAs ? (
            <div className="noteBlock tinted">
              <div className="noteHead"><span className="eyebrow">Acting as</span><span className="noteValue">{currentUser?.displayName || currentUser?.email}</span></div>
              <div className="noteBody">You are viewing and editing {currentUser?.displayName}'s data. Switch back to your own account when done.</div>
            </div>
          ) : null}
        </>
      ) : null}

      <SectionHead label="Members" value={String(members.length)} />
      <div className="rowGroup">
        {members.map((member) => <LeaderRow key={member.id} label={member.displayName || member.email} value={String(member.role || '').replace(/^./, (c) => c.toUpperCase())} />)}
      </div>

    </section>
  );
}

function FeedbackDialog({ busy, kind, message, status, onChangeKind, onChangeMessage, onClose, onSubmit, screen }) {
  return (
    <div className="feedbackOverlay" role="presentation" onMouseDown={onClose}>
      <section className="feedbackDialog" role="dialog" aria-modal="true" aria-labelledby="feedback-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="feedbackHead">
          <div>
            <div className="eyebrow">Feedback on {screen}</div>
            <h1 className="pageTitle small" id="feedback-title">What should change?</h1>
          </div>
          <button className="closeButton" type="button" onClick={onClose} aria-label="Close feedback"><X size={16} strokeWidth={2.2} /></button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="chipRow">
            <button className={kind === 'bug' ? 'chip on' : 'chip'} type="button" onClick={() => onChangeKind('bug')}>Bug</button>
            <button className={kind === 'change' ? 'chip on' : 'chip'} type="button" onClick={() => onChangeKind('change')}>Change</button>
          </div>
          <div className="searchField" style={{ paddingTop: '12px' }}>
            <textarea className="field" autoFocus value={message} onChange={(event) => onChangeMessage(event.target.value)} placeholder="Describe the bug or the change you want" rows={7} />
          </div>
          <div className="feedbackFoot">
            <span>Sent with this screen's URL</span>
            <button className="btn live" type="submit" disabled={busy || !message.trim()}>{busy ? 'Sending' : 'Send'}</button>
          </div>
        </form>
        <div className="statusLine">{status}</div>
      </section>
    </div>
  );
}

createRoot(document.getElementById('root')).render(<App />);
